import { useEffect, useState } from "react";
import { FaPlus, FaEdit, FaSpinner } from "react-icons/fa";
import useAuthStore from "../../services/authStore";
import "./cashlimit.css";
import editIcon from "../../assets/icons/edit.png";
import backIcon from "../../assets/icons/back.png";
import { toastSuccess, toastError, toastWarning } from "../../services/toaster";

const API_URL = process.env.REACT_APP_API_URL;

const CashLimit = () => {
  const companyId = useAuthStore((state) => state.companyId);
  const token = useAuthStore((state) => state.token);

  const [cashLimits, setCashLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    currency: "",
    cash_limit: "",
    limit_type: "",
  });

  const fetchCashLimits = async () => {
    if (!companyId) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${API_URL}/company-cash-limit/${companyId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token && {
              Authorization: `Bearer ${token}`,
            }),
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch cash limits");
      }

      const data = await response.json();

      setCashLimits(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching cash limits:", error);
      setCashLimits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCashLimits();
  }, [companyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNew = () => {
    setEditingId(null);

    setFormData({
      currency: "",
      cash_limit: "",
      limit_type: "",
    });

    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);

    setFormData({
      currency: item.currency || "",
      cash_limit: item.cash_limit ?? "",
      limit_type: item.limit_type || "",
    });

    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);

    setFormData({
      currency: "",
      cash_limit: "",
      limit_type: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!companyId) {
      toastWarning("Company ID is missing.");
      return;
    }

    if (!formData.currency || !formData.cash_limit || !formData.limit_type) {
      toastWarning("Please fill all required fields.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        company_id: Number(companyId),
        currency: formData.currency,
        cash_limit: Number(formData.cash_limit),
        limit_type: formData.limit_type,
      };

      let url;
      let method;

      if (editingId) {
        url = `${API_URL}/company-cash-limit/${companyId}/${editingId}`;
        method = "PUT";
      } else {
        url = `${API_URL}/company-cash-limit/${companyId}`;
        method = "POST";
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        console.error("Cash limit API error:", errorData);

        throw new Error(
          errorData?.detail?.[0]?.msg ||
            errorData?.detail ||
            "Failed to save cash limit",
        );
      }

      await fetchCashLimits();
      toastSuccess(
        editingId
          ? "Cash limit updated successfully."
          : "Cash limit added successfully.",
      );
      handleCancel();
    } catch (error) {
      console.error("Error saving cash limit:", error);
      toastError(error.message || "Failed to save cash limit.");
    } finally {
      setSaving(false);
    }
  };

  if (showForm) {
    return (
      <div className="cashlimit-container">
        <div className="cashlimit-form-header">
          <button
            type="button"
            className="cashlimit-circle-back-btn"
            onClick={handleCancel}
          >
            <img src={backIcon} alt="Back" className="cashlimit-back-icon" />
          </button>

          <h3>{editingId ? "Edit Cash Limit" : "New Cash Limit"}</h3>
        </div>

        <div className="cashlimit-scroll-inner-body">
          <form onSubmit={handleSubmit}>
            <div className="cashlimit-form-row">
              <div className="cashlimit-form-group">
                <label>
                  Currency <span className="cashlimit-required-star">*</span>
                </label>

                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Currency</option>
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="AED">AED</option>
                </select>
              </div>

              <div className="cashlimit-form-group">
                <label>
                  Cash Limit <span className="cashlimit-required-star">*</span>
                </label>

                <input
                  type="number"
                  name="cash_limit"
                  value={formData.cash_limit}
                  onChange={handleChange}
                  placeholder="Enter cash limit"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="cashlimit-form-group">
                <label>
                  Limit Type <span className="cashlimit-required-star">*</span>
                </label>

                <select
                  name="limit_type"
                  value={formData.limit_type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Limit Type</option>
                  <option value="Daily">Daily</option>
                  <option value="Monthly">Monthly</option>
                </select>
              </div>
            </div>

            <div className="cashlimit-form-actions">
              <button
                type="button"
                className="cashlimit-cancel-btn"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="cashlimit-save-btn"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FaSpinner className="cashlimit-spinner" />
                    Saving...
                  </>
                ) : editingId ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="cashlimit-container">
      <div className="cashlimit-header">
        <button type="button" className="cashlimit-add-btn" onClick={handleNew}>
          <FaPlus />
          New Limit
        </button>
      </div>

      <div className="cashlimit-table-wrapper">
        {loading ? (
          <div className="cashlimit-center-loader">
            <FaSpinner className="cashlimit-spinner" />
            <span>Loading...</span>
          </div>
        ) : (
          <table className="cashlimit-table">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Cash Limit</th>
                <th>Limit Type</th>
                <th className="cashlimit-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {cashLimits.length > 0 ? (
                cashLimits.map((item) => (
                  <tr key={item.id}>
                    <td>{item.currency || "-"}</td>

                    <td>{item.cash_limit ?? "-"}</td>

                    <td>{item.limit_type || "-"}</td>

                    <td className="cashlimit-center">
                      <button
                        type="button"
                        className="cashlimit-edit-btn"
                        onClick={() => handleEdit(item)}
                        title="Edit"
                      >
                        <img src={editIcon} alt="Edit" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="cashlimit-no-data">
                    No cash limit found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CashLimit;
